// react
import Link from "next/link";
import Linkify from "linkify-react";

// views
import UserLink from "~/components/user/UserLink";
import UserAvatar from "~/components/user/UserAvatar";
import PostLink from "~/components/post/PostLink";
import RichContent from "~/components/app/RichContent";
import QuoteContent from "~/components/app/QuoteContent";
import { Avatar } from "antd";

// utils
import { timeAgo } from "~/utils/app";

const issueHint = (
  <p>
    请您仔细阅读《<a href="https://eleduck.com/posts/6GzfGe">电鸭社区规则</a>
    》并遵守。 社区建立不易，还请理解。如需申诉沟通，请发邮件至{" "}
    <a href="mailto:eleduck@qq.com">eleduck@qq.com</a> (注明：申诉)
  </p>
);

const MessageBase = (props) => {
  return (
    <div className="list-item post-item">
      <div className="aside">
        {props.message.user && <UserAvatar user={props.message.user} />}
      </div>
      <div className="body">{props.children}</div>
    </div>
  );
};

const UserViewed = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>查看了你的联系方式</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>
  </MessageBase>
);

const UserMuted = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>禁言了你</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <div className="mt-10">
      <p>
        亲，您已经被管理员禁言 {message.details.ban_days} 天并警告啦。原因是：
        <Linkify
          options={{
            target: "_blank",
          }}
        >
          {message.details.ban_reason}
        </Linkify>
      </p>
      {issueHint}
    </div>
  </MessageBase>
);

const UserUpgrade = ({ message }) => (
  <div className="list-item post-item">
    <div className="aside">
      <Avatar
        src={
          "https://duckfiles-dev.oss-cn-qingdao.aliyuncs.com/eleduck/avatar/defaults/4.jpg!64"
        }
      />
    </div>
    <div className="body">
      <div className="meta-info">
        <span>恭喜你升级啦！</span>
        <span>{timeAgo(message.created_at)}</span>
      </div>

      <div className="mt-10">
        <p>
          <Linkify
            options={{
              target: "_blank",
            }}
          >
            {message.details.content}
          </Linkify>
        </p>
      </div>
    </div>
  </div>
);

const PostMarked = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>收藏了你的帖子</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <h3 className="mt-10">
      <PostLink post={message.details.post} />
    </h3>
  </MessageBase>
);

const PostPinned = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>{`置顶了你的帖子到全站，电量值 +${
        message.details.ele || 2
      }；经验值 +${message.details.exp || 2}`}</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <div className="mt-10">
      <p>
        亲，您的帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》已经被管理员置顶啦！期待你更精彩的内容，一起冲鸭！
      </p>
    </div>
  </MessageBase>
);

const PostPinnedInCategory = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>{`置顶了你的帖子到本版，电量值 +${
        message.details.ele || 1
      }；经验值 +${message.details.exp || 1}`}</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <div className="mt-10">
      <p>
        亲，您的帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》已经被管理员置顶啦！期待你更精彩的内容，一起冲鸭！
      </p>
    </div>
  </MessageBase>
);

const PostDeleted = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>删除了你的帖子</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <div className="mt-10">
      <p>
        亲，您的帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》已经被管理员删除并警告啦，原因是：
        <Linkify
          options={{
            target: "_blank",
          }}
        >
          {message.details.reason}
        </Linkify>
      </p>
      {issueHint}
    </div>
  </MessageBase>
);

const PostCharged = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>为你充电</span>
      <span>
        <Link
          legacyBehavior
          href="/ele_records"
        >
          <a>
            <span>电量值 +{message.details.ele}</span>
          </a>
        </Link>
      </span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <h3 className="mt-10">
      <PostLink post={message.details.post} />
    </h3>
  </MessageBase>
);

const PostCommentCreated = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>评论了你的帖子</span>
      <span>
        <PostLink
          post={message.details.post}
          altTitle
        />
      </span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <QuoteContent>
      <RichContent html={message.details.comment.content} />
    </QuoteContent>
  </MessageBase>
);

const PostCommentCreatedWithEle = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>第一个评论了你的帖子</span>
      <span>
        <Link
          legacyBehavior
          href="/exp_records"
        >
          <a>
            <span>经验值 +{message.details.exp || "2"}</span>
          </a>
        </Link>
        &nbsp;&nbsp;
        <Link
          legacyBehavior
          href="/ele_records"
        >
          <a>
            <span>电量值 +{message.details.ele}</span>
          </a>
        </Link>
      </span>
      <span>
        <PostLink
          post={message.details.post}
          altTitle
        />
      </span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <QuoteContent>
      <RichContent html={message.details.comment.content} />
    </QuoteContent>
  </MessageBase>
);

const PostCommentVotedWithEle = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>赞了你在帖子</span>
      <span>
        <PostLink
          post={message.details.post}
          altTitle
        />
      </span>
      <span>下的评论</span>
      <span>
        <Link
          legacyBehavior
          href="/exp_records"
        >
          <a>
            <span>经验值 +{message.details.exp || "1"}</span>
          </a>
        </Link>
        &nbsp;&nbsp;
        <Link
          legacyBehavior
          href="/ele_records"
        >
          <a>
            <span>电量值 +{message.details.ele}</span>
          </a>
        </Link>
      </span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <QuoteContent>
      <RichContent html={message.details.comment.content} />
    </QuoteContent>
  </MessageBase>
);

const PostCommentDeleted = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>删除了你的评论，电量值 {message.details.ele ?? "-1"}</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <div className="mt-10">
      <p>
        亲，您在帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》下的评论已经被管理员删除并警告啦，原因是：
        <Linkify
          options={{
            target: "_blank",
          }}
        >
          {message.details.reason}
        </Linkify>
      </p>
      {issueHint}
    </div>
  </MessageBase>
);

const PostCommentReplied = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>回复了你在帖子</span>
      <span>
        <PostLink
          post={message.details.post}
          altTitle
        />
      </span>
      <span>下的评论</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <QuoteContent>
      <QuoteContent>
        <RichContent html={message.details.parent_comment.content} />
      </QuoteContent>

      <div className="mt-10">
        <RichContent html={message.details.comment.content} />
      </div>
    </QuoteContent>
  </MessageBase>
);

const PostCommentVoted = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>赞了你在帖子</span>
      <span>
        <PostLink
          post={message.details.post}
          altTitle
        />
      </span>
      <span>下的评论</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <QuoteContent>
      <RichContent html={message.details.comment.content} />
    </QuoteContent>
  </MessageBase>
);

const SiteNews = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>发布了系统通知</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <p className="mt-10">{message.details.title}</p>
    <QuoteContent>
      <RichContent html={message.details.content} />
    </QuoteContent>
  </MessageBase>
);

const PostHided = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>隐藏了你的帖子</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <div className="mt-10">
      <p>
        亲，您的帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》已经被管理员隐藏啦，原因是：
        <Linkify
          options={{
            target: "_blank",
          }}
        >
          {message.details.reason}
        </Linkify>
      </p>
      {issueHint}
    </div>
  </MessageBase>
);

const PostFeatured = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>{`加精了你的帖子，电量值 +${message.details.ele || 2}；经验值 +${
        message.details.exp || 2
      }`}</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <div className="mt-10">
      <p>
        亲，您的帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》已经被管理员加精啦！
      </p>
    </div>
  </MessageBase>
);

const PostNotFeatured = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>取消加精了你的帖子</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <div className="mt-10">
      <p>
        亲，您的帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》已经被管理员取消加精！
      </p>
    </div>
  </MessageBase>
);

const composeStatusChangeMessage = (message, statusText) => {
  return (
    <>
      <span>
        职位
        <a
          href={`/tposts/${message.details.opening.eleduck_post_id}`}
          className="text-primary"
        >
          《{message.details.opening.title}》
        </a>
        的申请状态变更为"{statusText}"
      </span>
    </>
  );
};
const TalentHrApprove = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "面试中")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        恭喜呀，您的申请已通过雇主审核，请等待 HR 与您联系沟通面试相关事宜。
      </p>
    </div>
  </MessageBase>
);

const TalentHrAccept = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "Offer 已接受")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>{`恭喜您已被录用。请安心准备入职，并联系人才库负责人领取特殊礼物。`}</p>
    </div>
  </MessageBase>
);

const TalentHrHire = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "已录用")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>{`恭喜您已入职，本次求职流程已全部结束，感谢您使用远程人才库系统！`}</p>
    </div>
  </MessageBase>
);

const TalentAdminApprove = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "雇主审核中")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>您的职位申请已通过人才库评估，请等待雇主对您进行审核</p>
    </div>
  </MessageBase>
);

const TalentAdminReject = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "人才库评估未通过")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        很遗憾！人才库评估未通过您的职位申请，
        {message.details.reason && (
          <>
            原因是：
            <Linkify
              options={{
                target: "_blank",
              }}
            >
              {message.details.reason}
            </Linkify>
            。
          </>
        )}
        没关系，我们会继续为您留意并推荐更合适的机会。
      </p>
    </div>
  </MessageBase>
);

const TalentHrContactingReject = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "雇主审核未通过")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        很遗憾，您的此次申请未能通过雇主的评估审核，
        {message.details.reason && (
          <>
            原因是：
            <Linkify
              options={{
                target: "_blank",
              }}
            >
              {message.details.reason}
            </Linkify>
            ，
          </>
        )}
        请理解。 不过没关系，人才库之后会继续留意推荐更适合您的机会。
      </p>
    </div>
  </MessageBase>
);

const TalentHrInterviewingReject = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "面试未通过")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        遗憾呀，您没有通过本次的面试，
        {message.details.reason && (
          <>
            原因是：
            <Linkify
              options={{
                target: "_blank",
              }}
            >
              {message.details.reason}
            </Linkify>
            ，
          </>
        )}
        请理解。不过没关系，人才库之后会继续留意推荐更适合您的机会。
      </p>
    </div>
  </MessageBase>
);

const TalentHrOfferingReject = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "Offer 沟通未通过")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        很遗憾，您的此次的 Offer 沟通未通过，
        {message.details.reason && (
          <>
            原因是：
            <Linkify
              options={{
                target: "_blank",
              }}
            >
              {message.details.reason}
            </Linkify>
            ，
          </>
        )}
        不过没关系，人才库之后会继续留意推荐更适合您的机会。
      </p>
    </div>
  </MessageBase>
);

const TalentHrOfferedReject = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "Offer 未接受")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        很遗憾，您的此次 Offer 未接受，
        {message.details.reason && (
          <>
            原因是：
            <Linkify
              options={{
                target: "_blank",
              }}
            >
              {message.details.reason}
            </Linkify>
            ，
          </>
        )}
        不过没关系，人才库之后会继续留意推荐更适合您的机会。
      </p>
    </div>
  </MessageBase>
);

const TalentHrReject = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {/* <span>{`职位《${message.details.opening.title}》的申请状态变更为"雇主审核未通过"`}</span> */}
      {composeStatusChangeMessage(message, "雇主审核未通过")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        很遗憾，您的此次申请未能通过雇主的评估审核，
        {message.details.reason && (
          <>
            原因是：
            <Linkify
              options={{
                target: "_blank",
              }}
            >
              {message.details.reason}
            </Linkify>
            ，
          </>
        )}
        请理解。 不过没关系，人才库之后会继续留意推荐更适合您的机会。
      </p>
    </div>
  </MessageBase>
);

const TalentCandidateReject = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {/* <span>{`职位《${message.details.opening.title}》的申请状态变更为"已取消"`}</span> */}
      {composeStatusChangeMessage(message, "已取消")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        {`您已取消职位《${message.details.opening.title}》的申请，原因是：`}
        <Linkify
          options={{
            target: "_blank",
          }}
        >
          {message.details.reason}
        </Linkify>
      </p>
    </div>
  </MessageBase>
);

const TalentAdminRecommend = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>{`已推荐至职位《${message.details.opening.title}》`}</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>{`恭喜您，您已被人才库推荐至职位《${message.details.opening.title}》 ，请等待 HR 与您联系`}</p>
    </div>
  </MessageBase>
);

const target = (message) => {
  if (message.details.post)
    return (
      <>
        帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》
      </>
    );
  if (message.details.user)
    return (
      <>
        用户
        <UserLink user={message.details.user} />
        {message.details.others}
      </>
    );
  if (message.details.group) return <>群组《{message.details.group.name}》</>;
};

const ReportDisposed = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>您的举报已被受理，电量值 +{message.details.ele || 5}</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        您是首位举报{target(message)}的用户，已受理。电量 +
        {message.details.ele || 5}
        ，感谢您对社区建设的贡献，Respect！
      </p>
    </div>
  </MessageBase>
);
const OthersReportDisposed = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>您的举报已被受理，电量值 +{message.details.ele || 2}</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        您对{target(message)}
        的举报已成功受理，电量 +{message.details.ele || 2}
        ，感谢您对社区建设的贡献，Respect！
      </p>
    </div>
  </MessageBase>
);

const EleRewarded = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>电量奖励，电量值 +{message.details.ele}</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        恭喜您，社区奖励您电量值 +{message.details.ele}
        {message.details.reason && `，原因：${message.details.reason}`}
      </p>
    </div>
  </MessageBase>
);
const TalentHrOffering = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "Offer 沟通中")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        恭喜呀，您已成功通过雇主的面试，接下来，HR 会与您沟通 offer 相关事项。
      </p>
    </div>
  </MessageBase>
);

const TalentHrOffered = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      {composeStatusChangeMessage(message, "Offer 已发放")}
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        恭喜呀，您已成功拿到了本次申请的
        offer。请综合考虑后，3日内给雇主最终回复。如需答疑解惑or/咨询帮助，可联系站长@大灰微信：17702953046
      </p>
    </div>
  </MessageBase>
);

const PostTransparent = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>帖子审核中</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>

    <div className="mt-10">
      <p>
        您发布的帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》正在审核，预计将在 24
        小时内审核完成，请耐心等待。如有紧急情况请联系站长沟通处理。
      </p>
    </div>
  </MessageBase>
);

const PostReverted = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>恢复了您的帖子</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        您发布的帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》已恢复显示。
      </p>
    </div>
  </MessageBase>
);

const PostRevertRequestDenied = ({ message }) => (
  <MessageBase message={message}>
    <div className="meta-info">
      <span>
        <UserLink user={message.user} />
      </span>
      <span>驳回了您的帖子恢复请求</span>
      <span>{timeAgo(message.created_at)}</span>
    </div>
    <div className="mt-10">
      <p>
        您对帖子《
        <PostLink
          post={message.details.post}
          isText
        />
        》的恢复请求已被驳回
        {message.details.reason && (
          <>
            原因是：
            <Linkify
              options={{
                target: "_blank",
              }}
            >
              {message.details.reason}
            </Linkify>
          </>
        )}
      </p>
    </div>
  </MessageBase>
);

export default {
  UserViewed,
  UserMuted,
  UserUpgrade,
  PostCharged,
  PostMarked,
  PostPinned,
  PostPinnedInCategory,
  PostDeleted,
  PostHided,
  PostCommentCreated,
  PostCommentCreatedWithEle,
  PostCommentDeleted,
  PostCommentReplied,
  PostCommentVoted,
  PostCommentVotedWithEle,
  SiteNews,
  PostFeatured,
  PostNotFeatured,
  TalentHrAccept,
  TalentHrApprove,
  TalentAdminApprove,
  TalentHrHire,
  TalentAdminReject,
  TalentHrReject,
  TalentCandidateReject,
  TalentAdminRecommend,
  ReportDisposed,
  OthersReportDisposed,
  EleRewarded,
  TalentHrOffering,
  TalentHrOffered,
  TalentHrContactingReject,
  TalentHrInterviewingReject,
  TalentHrOfferingReject,
  TalentHrOfferedReject,
  PostTransparent,
  PostReverted,
  PostRevertRequestDenied,
};
